import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ClipboardCheck, Calendar, FileText, Clock, Users, Rocket } from "lucide-react";
import Footer from "@/components/Footer";
import Sparkles from "@/components/Sparkles";

const LandingPage = () => {
  const navigate = useNavigate();

  // When opened from the installed desktop (Electron) or mobile PWA, skip the
  // marketing page entirely and go straight to the login screen.
  useEffect(() => {
    const isStandalonePWA =
      typeof window !== "undefined" &&
      (window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true);
    const isElectron =
      typeof window !== "undefined" &&
      (window.process?.versions?.electron ||
        /electron/i.test(window.navigator.userAgent));
    if (isStandalonePWA || isElectron) {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  const features = [
    {
      icon: <ClipboardCheck className="w-8 h-8" />,
      title: "Inspections with Photos",
      description: "Capture before/after photos, signatures, and detailed checklists on-site"
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: "Smart Scheduling",
      description: "Plan jobs, assign crews, and avoid double-bookings — all from one calendar"
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Invoices & Quotes",
      description: "Send branded quotes and invoices, track payments, and get paid faster"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Time Tracking & Payroll",
      description: "Employees clock in & out from any device — exportable timesheets included"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Customer Management",
      description: "Keep every client's history, notes, and recurring jobs in one tidy place"
    }
  ];

  return (
    <div
      className="min-h-screen"
      style={{
        background:
          'linear-gradient(180deg, #eaf3ff 0%, #d7e8ff 45%, #eaf3ff 100%)',
      }}
    >
      {/* Header */}
      <div
        className="border-b py-4 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)',
          borderColor: 'rgba(255,255,255,0.15)',
        }}
      >
        {/* Twinkling sparkles in the header */}
        <Sparkles count={20} color="rgba(255,255,255,0.85)" minSize={5} maxSize={14} />

        <div className="max-w-6xl mx-auto px-6 flex justify-between items-center relative" style={{ zIndex: 1 }}>
          <h1 className="text-2xl font-bold drop-shadow-md" style={{ color: '#ffffff' }}>
            Maidly Business
          </h1>
          <div className="flex items-center gap-3">
            <button
              data-testid="launch-app-header-btn"
              onClick={() => navigate("/login")}
              className="flex items-center gap-2 px-6 py-2.5 font-semibold rounded-lg shadow-md transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
              style={{ background: '#ffffff', color: '#0073ea' }}
            >
              <Rocket className="w-4 h-4" />
              Launch App
            </button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div
        className="relative overflow-hidden text-white"
        style={{ background: 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)' }}
      >
        {/* Twinkling sparkle background — pure CSS, GPU accelerated */}
        <Sparkles count={40} color="rgba(255,255,255,0.85)" minSize={8} maxSize={20} />

        <div className="container mx-auto px-4 py-20 text-center relative" style={{ zIndex: 1 }}>
          <h1 className="text-4xl sm:text-6xl font-bold mb-6 drop-shadow-lg">
            Run Your Cleaning Business Smarter
          </h1>
          <p className="text-lg sm:text-xl text-blue-100 max-w-2xl mx-auto mb-10">
            Inspections, scheduling, invoices, expenses, time-tracking — all in one place.
            Purchase a license or start a free 15-day trial.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <button
              data-testid="hero-free-trial-btn"
              onClick={() => navigate("/purchase?tier=free")}
              className="flex items-center gap-2 px-8 py-4 bg-white text-blue-700 hover:bg-blue-50 font-bold text-lg rounded-lg shadow-xl transition-all duration-200 hover:-translate-y-0.5"
            >
              <Rocket className="w-5 h-5" />
              Start Free Trial
            </button>
            <button
              data-testid="purchase-license-btn"
              onClick={() => navigate("/purchase")}
              className="px-8 py-4 border-2 border-white text-white hover:bg-white/10 font-semibold text-lg rounded-lg transition-all duration-200"
            >
              Purchase License
            </button>
            <button
              data-testid="user-dashboard-btn"
              onClick={() => navigate("/user")}
              className="px-8 py-4 text-blue-100 hover:text-white font-medium text-base rounded-lg transition-all duration-200 underline-offset-4 hover:underline"
            >
              Activate License →
            </button>
          </div>
          <p className="text-sm text-blue-200 mt-5">
            No credit card required • Full access for 15 days
          </p>
        </div>
      </div>

      {/* Features / Pricing wrapper */}
      <div className="relative overflow-hidden">
        {/* Blue sparkles drifting across the soft blue background */}
        <Sparkles count={35} color="rgba(0,115,234,0.55)" minSize={6} maxSize={16} />

        <div className="container mx-auto px-4 py-16 relative" style={{ zIndex: 1 }}>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-16">
          {features.map((feature) => (
            <Card
              key={feature.title}
              className="shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              style={{
                background: 'rgba(255,255,255,0.75)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(0,115,234,0.15)',
              }}
            >
              <CardContent className="p-6 text-center">
                <div 
                  className="inline-flex items-center justify-center w-16 h-16 rounded-2xl text-white mb-4"
                  style={{ backgroundColor: '#0073ea' }}
                >
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Pricing Preview */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#0b2545' }}>
            Simple Pricing
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                tier: "Free Trial",
                price: "$0",
                duration: "15 Days",
                color: "#0073ea",
                features: ["Basic Dashboard", "Limited Reports", "Email Support"],
                cta: "Start Free Trial"
              },
              {
                tier: "Pro",
                price: "$150",
                duration: "1 Year",
                color: "linear-gradient(135deg, #0073ea 0%, #0b2545 100%)",
                features: ["Full Dashboard", "Advanced Reports", "Priority Support", "API Access", "Custom Branding"],
                popular: true,
                cta: "Purchase Now"
              }
            ].map((tier) => (
              <Card
                key={tier.tier}
                className={`shadow-sm hover:shadow-xl transition-all duration-300 relative ${
                  tier.popular ? "ring-2" : ""
                }`}
                style={{
                  background: 'rgba(255,255,255,0.85)',
                  backdropFilter: 'blur(10px)',
                  border: tier.popular
                    ? '1px solid #0073ea'
                    : '1px solid rgba(0,115,234,0.15)',
                }}
              >
                <CardContent className="p-8">
                  <div
                    className="text-center mb-6 p-4 text-white rounded-lg"
                    style={{ background: tier.color }}
                  >
                    <h3 className="text-2xl font-bold mb-1">{tier.tier}</h3>
                    <div className="text-4xl font-bold mb-1">{tier.price}</div>
                    <p className="text-sm opacity-90">{tier.duration}</p>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm text-gray-700">
                        <span style={{ color: '#0073ea' }} className="mt-1 font-bold">✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => navigate("/purchase")}
                    className="w-full py-4 text-white font-semibold transition-all duration-200"
                    style={{ backgroundColor: '#0073ea' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#005bb5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#0073ea'}
                  >
                    {tier.cta}
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* How It Works */}
        <div className="mt-20 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#0b2545' }}>
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Choose Your Plan",
                description: "Select between Free Trial or Pro tier based on your needs"
              },
              {
                step: "2",
                title: "Complete Payment",
                description: "Free trial is instant, Pro tier via secure PayPal checkout"
              },
              {
                step: "3",
                title: "Receive License",
                description: "License key delivered instantly to your email"
              }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div 
                  className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                  style={{ backgroundColor: '#0073ea' }}
                >
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-800">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LandingPage;